/** User-facing copy for finalize upload / generation progress modals. */

export const GENERATION_START_MESSAGE =
  "Starting generation — we're securing your files and preparing the declaration.";

const GENERATION_STATUS_ALIASES = {
  "starting declaration of records generation...":
    "Starting generation — validating your order and uploaded files.",
  "preparing source files...":
    "Reviewing uploaded files and preparing them for the final PDF.",
  "generating declaration files...":
    "Building the declaration and assembling your record package.",
  "generating declaration of records...":
    "Building the declaration and assembling your record package.",
  "declaration of records is ready.": "Declaration complete — finishing up your order.",
  "request timed out; still waiting for declaration generation on the server…":
    "Still working on the server — we'll update you as soon as generation continues.",
};

export function normalizeGenerationStatusMessage(rawMessage = "") {
  const trimmed = String(rawMessage || "").trim();
  if (!trimmed) {
    return "";
  }

  const key = trimmed.toLowerCase();
  return GENERATION_STATUS_ALIASES[key] || trimmed;
}

export function getUploadFlowCopy({
  recordType,
  isAuditType = false,
  fileCount = 1,
  uploadProgress = 0,
} = {}) {
  const isXray = recordType === "X-Ray";
  const isZipFlow = isXray || isAuditType;
  const count = Math.max(1, Number(fileCount) || 1);

  let title;
  if (isXray) {
    title = "Uploading imaging files";
  } else if (isAuditType) {
    title = "Uploading audit package";
  } else if (count > 1) {
    title = `Uploading ${count} files`;
  } else {
    title = "Uploading your file";
  }

  let subtitle;
  if (uploadProgress >= 100) {
    subtitle =
      "Upload complete. Saving files to your order — generation will start next.";
  } else if (uploadProgress >= 75) {
    subtitle = "Almost done. Finishing the last part of your upload.";
  } else if (uploadProgress >= 40) {
    subtitle = isZipFlow
      ? "Your ZIP is uploading securely. Please keep this window open."
      : "Files are transferring securely. Please keep this window open.";
  } else if (uploadProgress > 0) {
    subtitle = isZipFlow
      ? "Starting upload. Large ZIP files may take a few minutes."
      : "Upload in progress. Large files may take a few minutes.";
  } else {
    subtitle = isZipFlow
      ? "Preparing your package and connecting to secure storage."
      : "Preparing your upload. Do not close this window until upload reaches 100%.";
  }

  return {
    title,
    subtitle,
    progressLabel: "Upload progress",
  };
}

export function getGenerationFlowCopy({
  recordType,
  isAuditType = false,
  isNoRecords = false,
  generationProgress = 0,
  statusMessage = "",
} = {}) {
  const isXray = recordType === "X-Ray";
  const normalizedStatus = normalizeGenerationStatusMessage(statusMessage);

  let title;
  if (isNoRecords) {
    title = "Generating No Records declaration";
  } else if (isXray) {
    title = "Generating imaging declaration";
  } else if (isAuditType) {
    title = "Generating audit declaration";
  } else {
    title = "Generating declaration of records";
  }

  let subtitle;
  if (normalizedStatus) {
    subtitle =
      generationProgress >= 100
        ? normalizedStatus
        : "Creating your final PDF and declaration — this may take a few minutes.";
  } else if (generationProgress >= 100) {
    subtitle = "Declaration complete. Refreshing your order…";
  } else if (generationProgress >= 75) {
    subtitle = "Finalizing files and preparing your order for the next workflow step.";
  } else if (generationProgress >= 50) {
    subtitle = "Assembling documents and building the declaration PDF.";
  } else if (generationProgress >= 25) {
    subtitle = "Processing uploaded content and validating file structure.";
  } else if (generationProgress > 0) {
    subtitle = "Generation has started. Please keep this window open until it reaches 100%.";
  } else {
    subtitle = isNoRecords
      ? "We're creating the No Records Declaration. Please don't close this window."
      : isXray
        ? "Processing images and creating the declaration. Please don't close this window."
        : "We're creating your final record package. Please don't close this window.";
  }

  const detail =
    normalizedStatus &&
    generationProgress > 0 &&
    generationProgress < 100 &&
    normalizedStatus !== subtitle
      ? normalizedStatus
      : normalizedStatus && generationProgress >= 100
        ? normalizedStatus
        : "";

  return {
    title,
    subtitle,
    detail,
    progressLabel: "Generation progress",
  };
}
